/**
 * Per-locale slugs for MLM plan hreflang/alternates.
 * Key: plan image path (used to identify the plan). Value: locale -> slug.
 * Used when a plan has different translated URL slugs per locale.
 */
const PLAN_HREFLANG_SLUGS: Record<string, Record<string, string>> = {
  '/images/mlm-crowd-funding-plan.jpg': {
    en: 'crowd-funding-plan-mlm-software',
    es: 'software-mlm-para-planes-de-financiacion-colectiva',
    fr: 'logiciel-mlm-de-plan-de-financement-participatif',
    it: 'software-mlm-del-piano-di-finanziamento-della-folla',
    de: 'crowd-funding-plan-mlm-software',
    pt: 'crowd-funding-plan-mlm-software',
    zh: 'crowd-funding-plan-mlm-software',
  },
  '/images/mlm-party-plan.jpg': {
    en: 'party-plan-mlm-software',
    es: 'software-mlm-para-planificar-fiestas',
    fr: 'logiciel-mlm-plan-de-fete',
    it: 'software-mlm-per-piano-feste',
    de: 'party-plan-mlm-software',
    pt: 'party-plan-mlm-software',
    zh: 'party-plan-mlm-software',
  },
  '/images/binary-mlm-plan.jpg': {
    en: 'binary-mlm-software',
    es: 'plan-binario',
    fr: 'plan-binaire',
    it: 'piano-binario',
    de: 'binaere-plaene',
    pt: 'binary-mlm-software',
    zh: 'binary-mlm-software',
  },
  '/images/mlm-spillover-binary-plan.jpg': {
    en: 'spillover-binary-plan-mlm-software',
    es: 'software-mlm-de-plan-binario-de-desbordamiento',
    fr: 'logiciel-mlm-de-plan-binaire-de-debordement',
    it: 'software-mlm-per-piani-binari-di-spillover',
    de: 'spillover-binaerplan-mlm-software',
    pt: 'spillover-binary-plan-mlm-software',
    zh: 'spillover-binary-plan-mlm-software',
  },
  '/images/mlm-xup-plan.jpg': {
    en: 'x-up-plan-mlm-software',
    es: 'software-mlm-plan-x-up',
    fr: 'software-x-up-plan-mlm',
    it: 'software-mlm-del-piano-x-up',
    de: 'x-up-plan-mlm-software',
    pt: 'x-up-plan-mlm-software',
    zh: 'x-up-plan-mlm-software',
  },
};

const LOCALES = ['en', 'es', 'fr', 'de', 'it', 'pt', 'zh'] as const;

/** Reverse map: (locale, slug) -> plan image path. Used to resolve plan by translated URL slug. */
let _localeSlugToImage: Record<string, string> | null = null;
function getLocaleSlugToImage(): Record<string, string> {
  if (_localeSlugToImage) return _localeSlugToImage;
  const map: Record<string, string> = {};
  for (const [image, localeSlugs] of Object.entries(PLAN_HREFLANG_SLUGS)) {
    for (const [loc, slug] of Object.entries(localeSlugs)) {
      map[`${loc}:${slug}`] = image;
    }
  }
  _localeSlugToImage = map;
  return map;
}

/**
 * Returns plan image path for a (locale, slug) if this is a known translated plan slug; otherwise null.
 * Use to load plan by image when DB has no row for that slug in that locale.
 */
export function getPlanImageByLocaleSlug(locale: string, slug: string): string | null {
  return getLocaleSlugToImage()[`${locale}:${slug}`] ?? null;
}

/**
 * Returns locale -> slug map for hreflang if this plan has custom translated slugs; otherwise null.
 */
export function getPlanHreflangSlugs(planImage: string | undefined): Record<string, string> | null {
  if (!planImage) return null;
  return PLAN_HREFLANG_SLUGS[planImage] ?? null;
}

/**
 * Build alternates.languages for an MLM plan: either from custom map or default (same slug for all).
 */
export function getPlanAlternateLanguages(
  baseUrl: string,
  currentSlug: string,
  currentLocale: string,
  planImage: string | undefined
): Record<string, string> {
  const custom = getPlanHreflangSlugs(planImage);
  const languages: Record<string, string> = {};
  for (const loc of LOCALES) {
    const slug = custom?.[loc] ?? currentSlug;
    if (loc === 'en') {
      languages[loc] = `${baseUrl}/${slug}`;
    } else {
      languages[loc] = `${baseUrl}/${loc}/${slug}`;
    }
  }
  return languages;
}
